import {useEffect,useMemo,useRef,useState} from 'react';
import {readPendingGuestBooking,clearPendingGuestBooking} from './PendingGuestBooking';
import {bookingPath,dateFromBookingPath} from './BookingLocationRoutes';
import {LoginScreen,type AccountAccess} from './LoginScreen';
import {RoomFormattedText} from './RoomFormattedText';
import {PublicWaitlistPanel} from './PublicWaitlistPanel';

type Location={code:string;name:string;timezone:string;phone?:string|null};
type Service={code:string;name:string;durationMinutes:number;cleanupMinutes:number};
type Slot={startsAt:string;serviceEndsAt:string;occupancyEndsAt:string;
  availableResourceCount:number;rooms?:Array<{code:string;name:string}>};
type ServiceSlot=Slot&{serviceCode:string};
type Selection={date:string;slot:ServiceSlot;room:{code:string;name:string};serviceCode:string};
const selectionKey=(s:Selection)=>[s.date,s.room.code,s.slot.startsAt,s.serviceCode].join('|');
type SlotChoice={startsAt:string;room:{code:string;name:string}};
type HeldBooking={status:'held';holdId:string;expiresAt:string;replayed:boolean;
  serviceName:string;roomName:string;locationName:string;priceCents:number|null};
const asMoney=(cents:number)=>new Intl.NumberFormat('en-US',{
  style:'currency',currency:'USD'}).format(cents/100);

const isoDay=(date:Date,timezone:string)=>{
  const parts=new Intl.DateTimeFormat('en-US',{
    timeZone:timezone,year:'numeric',month:'2-digit',day:'2-digit',
  }).formatToParts(date);
  const value=(name:string)=>parts.find(p=>p.type===name)?.value??'';
  return value('year')+'-'+value('month')+'-'+value('day');
};
const nextDay=(date:string,increment:number)=>
  new Date(Date.parse(date+'T12:00:00Z')+increment*86400000).toISOString().slice(0,10);
const localTime=(date:string,timeZone:string)=>
  new Intl.DateTimeFormat('en-US',{timeZone,hour:'numeric',minute:'2-digit'})
    .format(new Date(date));
const dayTitle=(date:string)=>
  new Intl.DateTimeFormat('en-US',{weekday:'short',month:'short',day:'numeric',year:'numeric',
    timeZone:'UTC'}).format(new Date(date+'T12:00:00Z'));
async function readJson<T>(url:string,signal?:AbortSignal):Promise<T>{
  const response=await fetch(url,{credentials:'same-origin',cache:'no-store',signal});
  if(!response.ok)throw Error('Unable to load availability.');
  const json=await response.json() as {ok:boolean;data?:T};
  if(!json.ok||!json.data)throw Error('Unable to load availability.');
  return json.data;
}

export function PublicBookingWorkspace({location,onBookingAuthenticated,isGuestAccount}:{
  location:Location;onBookingAuthenticated:(identity:AccountAccess)=>void;
  isGuestAccount:boolean
}){
  const [date,setDate]=useState(()=>
    dateFromBookingPath(window.location.pathname)??isoDay(new Date(),location.timezone));
  const chooseDate=(next:string)=>{
    setSlotChoice(null);
    setDate(next);
    window.history.replaceState({},'',bookingPath(location.code,next));
  };
  const [catalog,setCatalog]=useState<Service[]>([]);
  const [publishedRooms,setPublishedRooms]=useState<Array<{code:string;name:string}>>([]);
  // Empty filter is intentionally All Services; the owner does not want
  // a first-service default that hides other room/time service options.
  const [service,setService]=useState('');
  const [catalogStatus,setCatalogStatus]=useState<'loading'|'ready'|'error'>('loading');
  const [slots,setSlots]=useState<ServiceSlot[]>([]);
  const [status,setStatus]=useState<'loading'|'ready'|'error'>('loading');
  const [selections,setSelections]=useState<Selection[]>([]);
  const [showLogin,setShowLogin]=useState(false);
  const [joinWaitlist,setJoinWaitlist]=useState(false);
  const [checkoutRequested,setCheckoutRequested]=useState(false);
  const roomScrollRef=useRef<HTMLDivElement|null>(null);
  const [roomNav,setRoomNav]=useState({previous:false,next:false});
  const [slotChoice,setSlotChoice]=useState<SlotChoice|null>(null);
  const [roomInfo,setRoomInfo]=useState<{
    room:{code:string;name:string;publicDescription:string|null;
      roomNotice:string|null;hasPhoto:boolean}|null;
    state:'loading'|'ready'|'error';
  }|null>(null);
  const loadRoomInfo=async(code:string)=>{
    setRoomInfo({room:null,state:'loading'});
    try{
      const info=await readJson<{code:string;name:string;publicDescription:string|null;
        roomNotice:string|null;hasPhoto:boolean}>(
          '/api/v1/public/rooms/'+encodeURIComponent(code)+'?'+
          new URLSearchParams({location:location.code}));
      setRoomInfo({room:info,state:'ready'});
    }catch{setRoomInfo({room:null,state:'error'});}
  };
  const [nextBusy,setNextBusy]=useState(false);
  const [nextMessage,setNextMessage]=useState('');
  const nextRequest=useRef<AbortController|null>(null);
  const holdKeys=useRef(new Map<string,string>());
  const [holdEnabled,setHoldEnabled]=useState(false);
  const [creditConfirmationEnabled,setCreditConfirmationEnabled]=useState(false);
  const [slotRevision,setSlotRevision]=useState(0);
  const [holdBusy,setHoldBusy]=useState(false);
  const [holdMessage,setHoldMessage]=useState('');
  const [held,setHeld]=useState<Array<HeldBooking&{selection:Selection}>>([]);
  const heldRef=useRef<Array<HeldBooking&{selection:Selection}>>([]);
  const [expired,setExpired]=useState(false);
  const [credits,setCredits]=useState<{state:'loading'|'ready'|'error';
    items:Array<{serviceCode:string;remainingUses:number}>}>({
      state:'loading',items:[],
    });
  const [confirmed,setConfirmed]=useState<Array<{appointmentId:string;holdId:string}>>([]);
  const [confirmationBusy,setConfirmationBusy]=useState(false);
  const [confirmationMessage,setConfirmationMessage]=useState('');
  useEffect(()=>{
    const controller=new AbortController();
    void readJson<{holdEnabled:boolean;checkoutEnabled:boolean;
      creditConfirmationEnabled?:boolean}>(
      '/api/v1/guest/bookings/capabilities',controller.signal)
      .then(data=>{if(!controller.signal.aborted){
        setHoldEnabled(data.holdEnabled);
        setCreditConfirmationEnabled(data.creditConfirmationEnabled===true);
      }})
      .catch(()=>{if(!controller.signal.aborted)setHoldEnabled(false);});
    return ()=>controller.abort();
  },[]);
  useEffect(()=>{
    const pending=readPendingGuestBooking();
    const intended=pending?.location===location.code?pending:null;
    setDate(intended?.date??dateFromBookingPath(window.location.pathname)??
      isoDay(new Date(),location.timezone));setSelections([]);setSlotChoice(null);
    setService('');setCatalog([]);setCatalogStatus('loading');
    const controller=new AbortController();
    setPublishedRooms([]);
    void readJson<{rooms:Array<{code:string;name:string}>}>(
      '/api/v1/public/rooms?'+new URLSearchParams({location:location.code}),controller.signal)
      .then(data=>{if(!controller.signal.aborted&&Array.isArray(data.rooms))
        setPublishedRooms(data.rooms);}).catch(()=>{});
    setStatus('loading');setHeld([]);heldRef.current=[];setHoldMessage('');
    void readJson<{services:Service[]}>('/api/v1/services?'+new URLSearchParams({
      location:location.code}),controller.signal)
      .then(data=>{if(!controller.signal.aborted){
        if(!Array.isArray(data.services))throw Error('INVALID_CATALOG');
        setCatalog(data.services);setCatalogStatus('ready');
      }})
      .catch(()=>{if(!controller.signal.aborted){
        setCatalog([]);setCatalogStatus('error');setStatus('error');
      }});
    return ()=>controller.abort();
  },[location.code,location.timezone]);
  useEffect(()=>{
    if(catalogStatus!=='ready')return;
    const services=service?catalog.filter(s=>s.code===service):catalog;
    if(!services.length){setSlots([]);setStatus('ready');return;}
    const controller=new AbortController();
    setStatus('loading');setSlots([]);setSlotChoice(null);
    // Each service keeps the existing availability authority; aggregate only
    // verified room+start candidates so a cell can offer all valid services.
    void Promise.all(services.map(async item=>{
      const data=await readJson<{slots:Slot[]}>('/api/v1/slots?'+new URLSearchParams({
        location:location.code,service:item.code,date}),controller.signal);
      if(!Array.isArray(data.slots)||!data.slots.every(slot=>
        slot&&typeof slot.startsAt==='string'&&
        Number.isFinite(Date.parse(slot.startsAt))&&
        typeof slot.serviceEndsAt==='string'&&
        Number.isFinite(Date.parse(slot.serviceEndsAt))&&
        typeof slot.occupancyEndsAt==='string'&&
        Number.isFinite(Date.parse(slot.occupancyEndsAt))&&
        Array.isArray(slot.rooms)&&slot.rooms.every(room=>
          room&&typeof room.code==='string'&&room.code.length>0&&
          typeof room.name==='string'&&room.name.length>0)))
        throw Error('INVALID_SLOTS');
      return data.slots.map(slot=>({...slot,serviceCode:item.code}));
    })).then(batches=>{
      if(controller.signal.aborted)return;
      const available=batches.flat();
      setSlots(available);setStatus('ready');
      const pending=readPendingGuestBooking();
      if(pending?.location===location.code&&pending.date===date){
        const slot=available.find(s=>s.startsAt===pending.startsAt&&
          s.serviceCode===pending.service&&
          s.rooms?.some(room=>room.code===pending.roomCode));
        const room=slot?.rooms?.find(r=>r.code===pending.roomCode);
        if(slot&&room)setSelections(current=>current.some(item=>selectionKey(item)===
          selectionKey({date,slot,room,serviceCode:slot.serviceCode}))?current:
          [...current,{date,slot,room,serviceCode:slot.serviceCode}]);
        else{clearPendingGuestBooking();setHoldMessage(
          'That selected time is no longer available. Please choose another time.');}
      }
    }).catch(()=>{if(!controller.signal.aborted){
      setSlots([]);setStatus('error');
    }});
    return ()=>controller.abort();
  },[location.code,service,date,slotRevision,catalog,catalogStatus]);
  const rooms=useMemo(()=>{
    const entries=new Map<string,{code:string;name:string}>();
    for(const slot of slots)for(const r of slot.rooms??[])entries.set(r.code,r);
    for(const room of publishedRooms)entries.set(room.code,room);
    return publishedRooms.length?[...entries.values()].sort((a,b)=>{
      const ai=publishedRooms.findIndex(room=>room.code===a.code);
      const bi=publishedRooms.findIndex(room=>room.code===b.code);
      return (ai<0?Number.MAX_SAFE_INTEGER:ai)-
        (bi<0?Number.MAX_SAFE_INTEGER:bi);
    }):[...entries.values()].sort((a,b)=>a.name.localeCompare(b.name));
  },[slots,publishedRooms]);
  const times=useMemo(()=>[...new Set(slots.map(s=>s.startsAt))].sort(),[slots]);
  const updateRoomNav=()=>{
    const node=roomScrollRef.current;
    if(!node)return;
    setRoomNav({previous:node.scrollLeft>3,
      next:node.scrollLeft+node.clientWidth<node.scrollWidth-3});
  };
  useEffect(()=>{
    const node=roomScrollRef.current;
    if(!node)return;
    const observer=new ResizeObserver(updateRoomNav);
    observer.observe(node);
    requestAnimationFrame(updateRoomNav);
    return ()=>observer.disconnect();
  },[rooms.length,status]);
  const scrollRooms=(direction:-1|1)=>{
    const node=roomScrollRef.current;
    if(!node)return;
    node.scrollBy({left:direction*Math.max(170,Math.floor(node.clientWidth*0.75)),
      behavior:'smooth'});
  };
  // A single room/time can offer different service durations. Never infer
  // service identity from the first catalog entry or display a fake option.
  const choicesFor=(time:string,roomCode:string)=>{
    const found=new Map<string,ServiceSlot>();
    for(const slot of slots){
      if(slot.startsAt===time&&slot.rooms?.some(r=>r.code===roomCode))
        found.set(slot.serviceCode,slot);
    }
    return [...found.values()];
  };
  const activeChoices=slotChoice?choicesFor(slotChoice.startsAt,slotChoice.room.code):[];
  /** The server holds each selected physical room atomically, including cleanup.
   * A failed multi-room attempt releases earlier acquisitions rather than
   * implying that partially held appointments were booked. */
  const releaseIds=async(items:Array<HeldBooking&{selection:Selection}>,keepalive=false)=>{
    const outcomes=await Promise.all(items.map(async item=>{
      try{
        const response=await fetch('/api/v1/guest/bookings/holds/release',{
          method:'POST',credentials:'same-origin',cache:'no-store',keepalive,
          headers:{'Content-Type':'application/json'},
          body:JSON.stringify({holdId:item.holdId}),
        });
        return response.ok;
      }catch{return false;}
    }));
    return items.filter((_,index)=>!outcomes[index]);
  };
  const holdSelected=async()=>{
    if(!selections.length||holdBusy||held.length||expired)return;
    if(!holdEnabled){
      setHoldMessage('Online booking is temporarily unavailable. No time has been held.');
      return;
    }
    if(!isGuestAccount){setCheckoutRequested(true);setShowLogin(true);return;}
    setHoldBusy(true);setHoldMessage('');
    const acquired:Array<HeldBooking&{selection:Selection}>=[];
    try{
      // A pre-login tap is never a hold. On successful authentication,
      // requery exact service, date, resource and occupancy before any write.
      // The DB acquisition below independently enforces overlap atomically.
      const fresh=new Map<string,Slot[]>();
      for(const item of selections){
        const lookup=item.date+'|'+item.serviceCode;
        if(!fresh.has(lookup)){
          const page=await readJson<{slots:Slot[]}>('/api/v1/slots?'+
            new URLSearchParams({location:location.code,service:item.serviceCode,
              date:item.date}));
          if(!Array.isArray(page.slots))throw Error('Unable to verify the selected times.');
          fresh.set(lookup,page.slots);
        }
      }
      const unavailable=selections.filter(item=>!fresh.get(item.date+'|'+item.serviceCode)
        ?.some(slot=>slot.startsAt===item.slot.startsAt&&
          slot.occupancyEndsAt===item.slot.occupancyEndsAt&&
          slot.serviceEndsAt===item.slot.serviceEndsAt&&
          slot.rooms?.some(room=>room.code===item.room.code)));
      if(unavailable.length){
        // Remove only invalid picks. No selected time becomes occupied.
        const gone=new Set(unavailable.map(selectionKey));
        setSelections(current=>current.filter(item=>!gone.has(selectionKey(item))));
        for(const item of unavailable)holdKeys.current.delete(selectionKey(item));
        throw Error(unavailable.length===1?
          'That time is no longer available. Please choose another time.':
          'Some selected times are no longer available. Please choose other times.');
      }
      const items=selections.map(item=>{
        const id=selectionKey(item);
        const key=holdKeys.current.get(id)??crypto.randomUUID();
        holdKeys.current.set(id,key);
        return {location:location.code,service:item.serviceCode,
          roomCode:item.room.code,startsAt:item.slot.startsAt,idempotencyKey:key};
      });
      const response=await fetch('/api/v1/guest/bookings/holds/basket',{
        method:'POST',credentials:'same-origin',cache:'no-store',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({items}),
      });
      if(!response.ok){
        if(response.status===409)throw Error(
          'One of the selected times is no longer available. No times were held.');
        if(response.status===401)throw Error('Log in to book your reservations.');
        throw Error('Unable to verify your held times. Please try again.');
      }
      const result=await response.json() as {ok:boolean;data?:{
        holds:HeldBooking[]}};
      if(!result.ok||result.data?.holds?.length!==selections.length||
        result.data.holds.some(hold=>hold.status!=='held'))
        throw Error('Unable to verify your selected times. Please try again.');
      for(let i=0;i<selections.length;i++)
        acquired.push({...result.data.holds[i],selection:selections[i]});
      heldRef.current=[...acquired];
      setHeld([...acquired]);
      clearPendingGuestBooking();
      setHoldMessage('Your selected time'+(acquired.length===1?' is':'s are')+
        ' held for 10 minutes. The reservation is not confirmed yet.');
    }catch(error){
      const unreleased=await releaseIds(acquired);
      heldRef.current=unreleased;setHeld(unreleased);
      setHoldMessage(unreleased.length?
        'Unable to finish or release every hold. Use Start Over to release the remaining time.':
        error instanceof Error?error.message:'Unable to hold these times.');
      if(!unreleased.length){
        // A lost response may follow a successful DB commit. Retain stable
        // idempotency keys so the next attempt recovers the identical holds.
        setSlotRevision(v=>v+1);
      }
    }finally{setHoldBusy(false);}
  };
  useEffect(()=>{
    if(!isGuestAccount||!held.length)return;
    const controller=new AbortController();
    setCredits({state:'loading',items:[]});
    void readJson<{credits:Array<{serviceCode:string;remainingUses:number}>}>(
      '/api/v1/guest/credits',controller.signal).then(data=>{
      if(!controller.signal.aborted)setCredits({
        state:'ready',items:Array.isArray(data.credits)?data.credits:[],
      });
    }).catch(()=>{
      if(!controller.signal.aborted)setCredits({state:'error',items:[]});
    });
    return ()=>controller.abort();
  },[isGuestAccount,held]);
  const creditsCoverHeld=()=>{
    if(credits.state!=='ready')return false;
    const required=new Map<string,number>();
    for(const booking of held)required.set(booking.selection.serviceCode,
      (required.get(booking.selection.serviceCode)??0)+1);
    return [...required.entries()].every(([service,count])=>
      credits.items.filter(item=>item.serviceCode===service)
        .reduce((n,item)=>n+item.remainingUses,0)>=count);
  };
  const confirmWithCredit=async()=>{
    if(confirmationBusy||expired||!held.length||!creditsCoverHeld())return;
    setConfirmationBusy(true);setConfirmationMessage('');
    try{
      const response=await fetch('/api/v1/guest/bookings/confirm-with-credits',{
        method:'POST',credentials:'same-origin',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({location:location.code,holdIds:held.map(item=>item.holdId)}),
      });
      const body=await response.json() as {ok:boolean;data?:{
        appointments:Array<{appointmentId:string;holdId:string}>}};
      if(!response.ok||!body.ok||!body.data?.appointments?.length){
        if(response.status===409){
          setConfirmationMessage('One or more credits or selected times are no longer '+
            'available. Please review your account and start over if necessary.');
        }else setConfirmationMessage('Unable to confirm reservations. No credits were '+
          'used unless the server confirms the appointment.');
        return;
      }
      heldRef.current=[];
      setConfirmed(body.data.appointments);
      setHeld([]);setSelections([]);setExpired(false);
      setSlotRevision(v=>v+1);
      setConfirmationMessage('Reservations confirmed using your service credits.');
    }catch{
      // Do not claim a failed network response means no reservation was written.
      // The next identical request is idempotent and returns the same records.
      setConfirmationMessage('Confirmation could not be verified. Please check '+
        'your appointments before trying again.');
    }finally{setConfirmationBusy(false);}
  };
  const releaseHeld=async()=>{
    if(holdBusy)return false;
    setHoldBusy(true);setHoldMessage('');
    const unreleased=await releaseIds(heldRef.current);
    heldRef.current=unreleased;setHeld(unreleased);
    setHoldBusy(false);
    if(unreleased.length){
      setHoldMessage('Unable to release every selected time. Please try Start Over again.');
      return false;
    }
    setHoldMessage('Time released.');
    setSlotRevision(v=>v+1);
    holdKeys.current.clear();
    return true;
  };
  const startOver=async()=>{
    if(holdBusy)return;
    if(heldRef.current.length&&!(await releaseHeld()))return;
    setSelections([]);setSlotChoice(null);setShowLogin(false);
    setConfirmed([]);setConfirmationMessage('');
    setCheckoutRequested(false);setExpired(false);
    setHoldMessage('');holdKeys.current.clear();clearPendingGuestBooking();
    setSlotRevision(v=>v+1);
  };
  const continueSelection=()=>{
    if(!selections.length||holdBusy||held.length||expired)return;
    if(!isGuestAccount){setCheckoutRequested(true);setShowLogin(true);}
    else void holdSelected();
  };
  const chooseSlot=(slot:ServiceSlot,room:Selection['room'])=>{
    const next:Selection={date,slot,room,serviceCode:slot.serviceCode};
    setSelections(current=>{
      const id=selectionKey(next);
      if(current.some(item=>selectionKey(item)===id))return current;
      // The same physical room cannot provide two overlapping sessions.
      if(current.some(item=>item.room.code===next.room.code&&
        Date.parse(item.slot.startsAt)<Date.parse(next.slot.occupancyEndsAt)&&
        Date.parse(item.slot.occupancyEndsAt)>Date.parse(next.slot.startsAt))){
        setHoldMessage('That room is already selected for an overlapping session.');
        return current;
      }
      return [...current,next];
    });
    setSlotChoice(null);setHoldMessage('');
    clearPendingGuestBooking();
  };
  useEffect(()=>{
    if(!isGuestAccount||!checkoutRequested||holdBusy||held.length||expired)return;
    setCheckoutRequested(false);
    void holdSelected();
  },[isGuestAccount,checkoutRequested]);
  useEffect(()=>{
    if(!held.length)return;
    const check=()=>{
      if(heldRef.current.some(item=>Date.parse(item.expiresAt)<=Date.now())){
        setExpired(true);setShowLogin(false);setCheckoutRequested(false);
        setHoldMessage('Your 10-minute hold expired. Start over to select your times again.');
      }
    };
    check();
    const timer=window.setInterval(check,1000);
    let checking=false;
    const poll=async()=>{
      if(checking||!heldRef.current.length)return;
      checking=true;
      try{
        const page=await readJson<{holds:Array<{holdId:string;active:boolean}>}>(
          '/api/v1/guest/bookings/holds/status?'+new URLSearchParams({
            ids:heldRef.current.map(item=>item.holdId).join(','),
          }));
        if(page.holds.some(item=>!item.active)){
          setExpired(true);setCheckoutRequested(false);setShowLogin(false);
          setHoldMessage('One of your selected times was released or expired. '+
            'Start over to select available times again.');
        }
      }catch{/* Keep the exact server-side holds authoritative. */}
      finally{checking=false;}
    };
    const statusTimer=window.setInterval(()=>void poll(),15000);
    return ()=>{window.clearInterval(timer);window.clearInterval(statusTimer);};
  },[held]);
  useEffect(()=>{
    const abandon=()=>{
      const items=heldRef.current;
      if(!items.length)return;
      // Best-effort immediate release. The database TTL always remains the
      // authoritative expiry fallback when browsers terminate abruptly.
      for(const item of items){
        void fetch('/api/v1/guest/bookings/holds/release',{
          method:'POST',credentials:'same-origin',keepalive:true,
          headers:{'Content-Type':'application/json'},
          body:JSON.stringify({holdId:item.holdId}),
        }).catch(()=>{});
      }
      heldRef.current=[];
    };
    window.addEventListener('pagehide',abandon);
    window.addEventListener('ecfs:booking-abandon',abandon);
    return ()=>{
      window.removeEventListener('pagehide',abandon);
      window.removeEventListener('ecfs:booking-abandon',abandon);
      abandon();
    };
  },[]);
  const findNext=async()=>{
    if(nextBusy||!catalog.length)return;
    nextRequest.current?.abort();
    const controller=new AbortController();nextRequest.current=controller;
    setNextMessage('');setNextBusy(true);
    try{
      const services=service?catalog.filter(x=>x.code===service):catalog;
      for(let n=1;n<=30;n++){
        const future=nextDay(date,n);
        const results=await Promise.all(services.map(item=>
          readJson<{slots:Slot[]}>('/api/v1/slots?'+
            new URLSearchParams({location:location.code,service:item.code,date:future}),
            controller.signal)));
        if(controller.signal.aborted)return;
        if(results.some(x=>x.slots.some(slot=>slot.rooms?.length))){
          chooseDate(future);return;
        }
      }
      if(!controller.signal.aborted)setNextMessage('No available start times found in the next 30 days.');
    }catch{if(!controller.signal.aborted)setNextMessage('Unable to search future dates right now.');}
    finally{if(!controller.signal.aborted)setNextBusy(false);}
  };
  return <section className={"ecfs-public-content ecfs-public-booking-screen"+
    (joinWaitlist?" is-waitlist-open":"")}
    aria-label="Public booking calendar">
    {joinWaitlist&&<PublicWaitlistPanel location={location} date={date}
      isGuestAccount={isGuestAccount} onLogin={()=>setShowLogin(true)}
      onClose={()=>setJoinWaitlist(false)}/>}
    <div className="ecfs-public-context">
      <p>You're booking for <strong>{location.name.toUpperCase()}</strong>.
        Use <strong>Choose a Different Location</strong> above to switch.</p>
      {location.phone&&<p>Questions? Call/text <a href={'tel:'+location.phone}>
        {location.phone}</a></p>}
    </div>
    <div className="ecfs-public-calendar-controls">
      <label className="ecfs-public-date-field">Booking date
        <input type="date" aria-label="Booking date" value={date}
          min={isoDay(new Date(),location.timezone)} onChange={e=>chooseDate(e.target.value)}/>
      </label>
      <div className="ecfs-public-day-buttons">
        <button type="button" aria-label="Previous booking day"
          disabled={date<=isoDay(new Date(),location.timezone)}
          onClick={()=>chooseDate(nextDay(date,-1))}>‹</button>
        <button type="button" aria-label="Next booking day"
          onClick={()=>chooseDate(nextDay(date,1))}>›</button>
      </div>
      <label className="ecfs-public-service-filter">Filter by Service
        <select aria-label="Filter by Service" value={service}
          onChange={e=>{setService(e.target.value);setSlotChoice(null);}}>
          <option value="">All Services</option>
          {catalog.map(s=><option key={s.code} value={s.code}>{s.name}</option>)}
        </select>
      </label>
    </div>
    <div className="ecfs-public-date-waitlist-actions">
      <h2>{dayTitle(date)}</h2>
      <button type="button" onClick={()=>setJoinWaitlist(true)}>Join Waitlist</button>
    </div>
    {status==='loading'&&<p role="status">Loading available times…</p>}
    {status==='error'&&<p role="alert">Booking availability could not be loaded.</p>}
    {status==='ready'&&times.length>0&&<div className="ecfs-public-room-navigation" aria-label="Navigate rooms">
      <button type="button" aria-label="Previous rooms" disabled={!roomNav.previous}
        onClick={()=>scrollRooms(-1)}>‹</button>
      <span>Rooms</span>
      <button type="button" aria-label="Next rooms" disabled={!roomNav.next}
        onClick={()=>scrollRooms(1)}>›</button>
    </div>}
    {status==='ready'&&times.length>0&&
      <div ref={roomScrollRef} onScroll={updateRoomNav}
        className="ecfs-public-rooms-scroll" tabIndex={0}
        aria-label="Room-by-room availability calendar">
        <div className="ecfs-public-room-grid" style={{
          gridTemplateColumns:'82px repeat('+rooms.length+',minmax(150px,1fr))'}}>
          <div className="ecfs-public-room-grid-heading">Time</div>
          {rooms.map(room=><div className="ecfs-public-room-grid-heading" key={room.code}>
            {room.name} <button type="button" className="ecfs-public-room-info-button"
              aria-label={'Information about '+room.name}
              onClick={()=>void loadRoomInfo(room.code)}>ⓘ</button>
          </div>)}
          {times.map(time=><div className="ecfs-public-room-grid-row"
            key={time} style={{display:'contents'}}>
            <div className="ecfs-public-room-time">{localTime(time,location.timezone)}</div>
            {rooms.map(room=>{
              const choices=choicesFor(time,room.code);
              const selected=selections.some(item=>item.date===date&&
                item.slot.startsAt===time&&item.room.code===room.code);
              return <div className="ecfs-public-room-cell" key={room.code}>
                {choices.length?<button type="button" aria-pressed={selected}
                  aria-label={'Choose services for '+room.name+' at '+
                    localTime(time,location.timezone)}
                  onClick={()=>setSlotChoice({startsAt:time,room})}>
                  <strong>{localTime(time,location.timezone)}</strong>
                  <small>{choices.length===1
                    ?catalog.find(s=>s.code===choices[0].serviceCode)?.name
                    :choices.length+' service options'}</small>
                </button>:<span className="ecfs-public-no-slot" aria-hidden="true"/>}
              </div>;
            })}
          </div>)}
        </div>
      </div>}
    {slotChoice&&status==='ready'&&<div className="ecfs-public-room-dialog-overlay"
      onClick={()=>setSlotChoice(null)}>
      <section role="dialog" aria-modal="true" aria-label="Choose a service"
        className="ecfs-public-room-dialog ecfs-public-service-choices"
        onClick={e=>e.stopPropagation()}>
        <button type="button" aria-label="Close service choices"
          onClick={()=>setSlotChoice(null)}>×</button>
        <h3>{slotChoice.room.name} · {localTime(slotChoice.startsAt,location.timezone)}</h3>
        <div className="ecfs-public-service-choice-list">
          {activeChoices.map(choice=><button type="button" key={choice.serviceCode}
            onClick={()=>chooseSlot(choice,slotChoice.room)}>
            {catalog.find(s=>s.code===choice.serviceCode)?.name??choice.serviceCode}
          </button>)}
        </div>
      </section>
    </div>}
    {roomInfo&&<div className="ecfs-public-room-dialog-overlay"
      onClick={()=>setRoomInfo(null)}>
      <section role="dialog" aria-modal="true" aria-label="Room information"
        className="ecfs-public-room-dialog" onClick={e=>e.stopPropagation()}>
        <button type="button" aria-label="Close room information"
          onClick={()=>setRoomInfo(null)}>×</button>
        {roomInfo.state==='loading'&&<p role="status">Loading room information…</p>}
        {roomInfo.state==='error'&&<p role="alert">Room information is unavailable.</p>}
        {roomInfo.state==='ready'&&roomInfo.room&&<>
          <h3>{roomInfo.room.name}</h3>
          {roomInfo.room.hasPhoto&&
            <img alt={roomInfo.room.name}
              src={'/api/v1/public/rooms/'+encodeURIComponent(roomInfo.room.code)+
                '/photo?'+new URLSearchParams({location:location.code})}/>}
          {roomInfo.room.publicDescription&&
            <RoomFormattedText content={roomInfo.room.publicDescription}/>}
        </>}
      </section>
    </div>}
    {status==='ready'&&catalog.length===0&&
      <p role="status">No online booking services are currently enabled for this location.</p>}
    {status==='ready'&&catalog.length>0&&times.length===0&&<div className="ecfs-public-no-availability"
      role="status">
      <strong>Not seeing any availability?</strong>
      <p>{location.phone?<>Call/text <a href={'tel:'+location.phone}>{location.phone}</a></>:
        'Please contact this location'} for last-minute bookings
        or to ask about other options.</p>
      <button type="button" disabled={nextBusy} onClick={()=>void findNext()}>
        {nextBusy?'Finding availability…':'Find the Next Available Day'}</button>
      {nextMessage&&<p role="status">{nextMessage}</p>}
      <p>Sign in to view your upcoming bookings.</p>
    </div>}
    <div className="ecfs-public-booking-next">
      {selections.length>0&&<div className="ecfs-public-booking-selections"
        aria-label="Selected reservations">
        {selections.map((item,index)=><div key={selectionKey(item)}
          className="ecfs-public-booking-selection">
          <span>{catalog.find(service=>service.code===item.serviceCode)?.name??item.serviceCode},
            {' '+item.room.name}, {dayTitle(item.date)} at
            {' '+localTime(item.slot.startsAt,location.timezone)}</span>
          {held.length===0&&!checkoutRequested&&<button type="button"
            aria-label={'Remove reservation '+(index+1)}
            onClick={()=>setSelections(current=>current.filter(selected=>
              selectionKey(selected)!==selectionKey(item)))}>Remove</button>}
        </div>)}
      </div>}
      {held.length>0&&<div className="ecfs-public-pending-checkout"
        aria-label="Booking hold details">
        <strong>{expired?'Booking holds expired':'Times temporarily held'}</strong>
        {held.map(item=><p key={item.holdId}>{item.serviceName}, {item.roomName},
          {' '+item.locationName}. Hold expires at
          {' '+localTime(item.expiresAt,location.timezone)}.
          {item.priceCents===null?'':(' Price: '+asMoney(item.priceCents))}</p>)}
        <p>{expired?'Your hold has expired. Start over and select available times again.':
          'Your selected times are held for up to 10 minutes. No reservation or payment has been completed yet.'}</p>
        {credits.state==='loading'&&<p role="status">Checking your service credits…</p>}
        {credits.state==='error'&&<p role="alert">Unable to verify your service credits.</p>}
        {!creditConfirmationEnabled&&<p>
          Final credit-backed reservation confirmation has not been enabled in this staging environment.
          No reservation has been made.
        </p>}
        {credits.state==='ready'&&!creditsCoverHeld()&&<p>
          There are not enough active service credits for these reservations.
          You can purchase service credits from the Store. No booking is confirmed.
        </p>}
        {creditConfirmationEnabled&&creditsCoverHeld()&&!expired&&<button type="button"
          disabled={confirmationBusy} onClick={()=>void confirmWithCredit()}>
          {confirmationBusy?'Confirming…':'Confirm '+held.length+
            ' '+(held.length===1?'Reservation':'Reservations')+
            ' with Service Credits'}
        </button>}
      </div>}
      {confirmed.length>0&&<p role="status">{confirmed.length} reservations confirmed. Appointment numbers:         {confirmed.map(item=>item.appointmentId).join(', ')}</p>}
      {confirmationMessage&&<p role="status">{confirmationMessage}</p>}
      {held.length===0&&confirmed.length===0&&<button type="button"
        disabled={!selections.length||holdBusy||expired}
        onClick={continueSelection}>
        {holdBusy?'Holding selected times…':
          selections.length?'Make '+selections.length+' '+
            (selections.length===1?'Reservation':'Reservations'):'Please Select A Time Slot'}
      </button>}
      {(selections.length>0||held.length>0||expired||confirmed.length>0)&&
        <button type="button" disabled={holdBusy}
          className="ecfs-public-booking-start-over"
          onClick={()=>void startOver()}>Start over</button>}
      {holdMessage&&<p role="status">{holdMessage}</p>}
      {showLogin&&!isGuestAccount&&<div className="ecfs-public-room-dialog-overlay"
        onClick={()=>{setShowLogin(false);setCheckoutRequested(false);}}>
        <section role="dialog" aria-modal="true" aria-label="Book your reservations"
          className="ecfs-public-booking-auth" onClick={event=>event.stopPropagation()}>
          <button type="button" aria-label="Close booking login"
            onClick={()=>{setShowLogin(false);setCheckoutRequested(false);}}>×</button>
          <h2>Log in to book</h2>
          <LoginScreen locationCode={location.code} onAuthenticated={identity=>{
            setShowLogin(false);onBookingAuthenticated(identity);
          }}/>
        </section>
      </div>}
    </div>
  </section>;
}
